import type { KeyResult } from "@aos/shared";
import { cn } from "@/lib/utils";

/**
 * Compute a 0..100 completion percentage for a Key Result.
 * Falls back to 0 when target/current are missing. A KR with target 0 is
 * treated as 100% when current >= 0 (nothing to do), else 0%.
 */
export function keyResultProgress(kr: Pick<KeyResult, "current_value" | "target_value">): number {
  const current = kr.current_value ?? 0;
  const target = kr.target_value;
  if (target == null) return 0;
  if (target === 0) return current >= 0 ? 100 : 0;
  const pct = (current / target) * 100;
  if (!Number.isFinite(pct)) return 0;
  return Math.max(0, Math.min(100, Math.round(pct)));
}

/**
 * Inline progress bar (no shadcn `progress` primitive exists in v0.1).
 * `value` is a 0..100 percentage.
 *
 * Design-system §5.5: black is the only accent — the filled track is always
 * `bg-primary`. Progress that is below 100% is slightly de-emphasised via
 * opacity rather than recoloured to green/amber; the unfilled track stays the
 * neutral `bg-muted`. No raw data hues here.
 */
export function ProgressBar({
  value,
  className,
}: {
  value: number;
  className?: string;
}) {
  const clamped = Math.max(0, Math.min(100, value));
  return (
    <div
      className={cn("h-2 w-full overflow-hidden rounded-full bg-muted", className)}
      role="progressbar"
      aria-valuenow={clamped}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div
        className={cn(
          "h-full rounded-full bg-primary transition-all",
          clamped < 100 && "opacity-80",
        )}
        style={{ width: `${clamped}%` }}
      />
    </div>
  );
}
